import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { toast } from "sonner";
import { RefreshCw, Search, Plus, ArrowLeft, Trash2, User, Building2, Users, X } from "lucide-react";
import KlantDetail from "./KlantDetail";
import CustomerTypeBadge, { CUSTOMER_TYPE_LABELS } from "../CustomerTypeBadge";
import { AdminPageHeader } from "../ui";

const STATUS_LABELS = {
  nieuw: "Nieuw", actief: "Actief", wacht_info: "Wacht op informatie",
  wacht_betaling: "Wacht op betaling", afgerond: "Afgerond", geblokkeerd: "Geblokkeerd",
};
// Fase 2 — primaire types bovenaan, legacy onderaan.
const PRIMARY_TYPES = ["zakelijk", "particulier", "onbekend"];
const LEGACY_TYPES = ["webshop", "shop_drop_ship", "terugkerend"];
const TYPE_LABELS = CUSTOMER_TYPE_LABELS;
const SOURCE_LABELS = {
  handmatig: "Handmatig", offerteformulier: "Offerteformulier",
  admin_import: "Admin import", webshop: "Webshop",
};

function StatusBadge({ status }) {
  const colors = {
    nieuw: "bg-blue-100 text-blue-700",
    actief: "bg-green-100 text-green-700",
    wacht_info: "bg-yellow-100 text-yellow-700",
    wacht_betaling: "bg-orange-100 text-orange-700",
    afgerond: "bg-gray-100 text-gray-700",
    geblokkeerd: "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${colors[status] || "bg-gray-100 text-gray-700"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function KlantenSection({ onNavigateSection, initialCustomerId } = {}) {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "", email: "",
    phone: "", whatsapp: "", customer_type: "particulier", status: "nieuw",
  });
  // Batch 3 · Fase 4 — Duplicate-warning UX state.
  //   duplicates: {has_strong, has_soft, strong[], soft[]} of null
  //   dupBlocked: true = de laatste create is geblokkeerd door een strong match,
  //     de UI toont dan de warning + "Toch aanmaken" knop.
  const [duplicates, setDuplicates] = useState(null);
  const [dupBlocked, setDupBlocked] = useState(false);
  const [selected, setSelected] = useState(initialCustomerId || null);
  const canCreate = hasPermission("customers:create");
  const canDelete = hasPermission("customers:delete");

  // Deeplink: navigeer direct naar klantdetail als initialCustomerId meekomt
  // (bv. vanuit "Klant openen" in factuur-detail).
  useEffect(() => {
    if (initialCustomerId) setSelected(initialCustomerId);
  }, [initialCustomerId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusF !== "all") params.status = statusF;
      if (typeF !== "all") params.customer_type = typeF;
      const r = await adminApi.get("/admin/customers", { params });
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, statusF, typeF]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async ({ force = false } = {}) => {
    if (!form.email && !form.phone) {
      toast.error("E-mail of telefoon is verplicht.");
      return;
    }
    try {
      const payload = { ...form };
      if (force) payload.confirm_duplicate = true;
      const r = await adminApi.post("/admin/customers", payload);
      toast.success(force ? "Klant aangemaakt (duplicaat bevestigd)" : "Klant aangemaakt");
      setCreating(false);
      setDuplicates(null);
      setDupBlocked(false);
      setForm({ first_name: "", last_name: "", company_name: "", email: "", phone: "", whatsapp: "", customer_type: "particulier", status: "nieuw" });
      setSelected(r.data.id);
    } catch (e) {
      // Batch 3 · Fase 4 — check op 409 duplicate_customer respons zodat de
      // UI de match-info kan tonen i.p.v. een generieke error.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "duplicate_customer") {
        setDuplicates(detail.duplicates || null);
        setDupBlocked(true);
        toast.warning(detail.message || "Er bestaat al een klant met deze gegevens.");
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  // Batch 3 · Fase 4 — Live (debounced) duplicate-check tijdens tikken.
  // Alleen ophalen als email of phone > 4 tekens is; retourneert soft +
  // strong zodat de UI meteen kan waarschuwen. Blokkeert nooit typen.
  useEffect(() => {
    if (!creating) return;
    const email = (form.email || "").trim();
    const phone = (form.phone || "").trim();
    if (email.length < 5 && phone.length < 5 && !(form.last_name && form.last_name.length >= 3) && !(form.company_name && form.company_name.length >= 3)) {
      setDuplicates(null);
      setDupBlocked(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await adminApi.post("/admin/customers/check-duplicates", {
          email: email || undefined,
          phone: phone || undefined,
          first_name: form.first_name || undefined,
          last_name: form.last_name || undefined,
          company_name: form.company_name || undefined,
        });
        if (!cancelled) {
          setDuplicates(r.data);
          // Als er geen strong match meer is, is de blokkade opgeheven.
          if (!r.data?.has_strong) setDupBlocked(false);
        }
      } catch {/* silent */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [creating, form.email, form.phone, form.first_name, form.last_name, form.company_name]);

  const onDelete = async (id) => {
    if (!window.confirm("Klant verwijderen? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      await adminApi.delete(`/admin/customers/${id}`);
      toast.success("Klant verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (selected) {
    return (
      <div data-testid="admin-section-klanten-detail">
        <button onClick={() => { setSelected(null); load(); }} className="inline-flex items-center text-sm text-[#0055FF] hover:underline mb-4" data-testid="klant-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug naar klantenlijst
        </button>
        <KlantDetail customerId={selected} onChanged={load} onNavigateSection={onNavigateSection} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-section-klanten">
      <AdminPageHeader
        icon={Users}
        title="Klanten"
        subtitle="Beheer klantenkaart, koppelingen en historie"
        actions={
          <>
            <Button onClick={load} variant="outline" size="sm" className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500" data-testid="klanten-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Vernieuwen
            </Button>
            {canCreate && (
              <Button onClick={() => setCreating(!creating)} data-testid="klant-new-button" className="rounded-full bg-brand-900 hover:bg-brand-700 text-white font-chivo font-bold uppercase text-xs shadow-brand-soft">
                <Plus className="w-4 h-4 mr-1.5" /> Nieuwe klant
              </Button>
            )}
          </>
        }
      />

      {creating && (
        <div className="mt-4 bg-white border border-gray-200 p-5" data-testid="klant-create-form">
          <h3 className="font-chivo font-bold text-[#0A192F] mb-3">Nieuwe klant aanmaken</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input data-testid="klant-form-firstname" placeholder="Voornaam" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="rounded-none" />
            <Input data-testid="klant-form-lastname" placeholder="Achternaam" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="rounded-none" />
            <Input data-testid="klant-form-company" placeholder="Bedrijfsnaam" value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="rounded-none" />
            <Input data-testid="klant-form-email" type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-none" />
            <Input data-testid="klant-form-phone" placeholder="Telefoon" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-none" />
            <Input data-testid="klant-form-whatsapp" placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({...form, whatsapp: e.target.value})} className="rounded-none" />
            <Select value={form.customer_type} onValueChange={(v) => setForm({...form, customer_type: v})}>
              <SelectTrigger data-testid="klant-form-type" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIMARY_TYPES.map((k) => <SelectItem key={k} value={k}>{TYPE_LABELS[k]}</SelectItem>)}
                <div className="border-t border-gray-100 my-1" />
                {LEGACY_TYPES.map((k) => <SelectItem key={k} value={k}>{TYPE_LABELS[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              onClick={() => onCreate({ force: dupBlocked })}
              data-testid={dupBlocked ? "klant-form-save-force" : "klant-form-save"}
              className={
                dupBlocked
                  ? "bg-amber-600 hover:bg-amber-700 text-white rounded-none font-chivo font-bold uppercase text-xs"
                  : "bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"
              }
            >
              {dupBlocked ? "Toch aanmaken (bewust duplicaat)" : "Aanmaken"}
            </Button>
            <Button onClick={() => { setCreating(false); setDuplicates(null); setDupBlocked(false); }} variant="outline" className="rounded-none">Annuleren</Button>
          </div>

          {/* Batch 3 · Fase 4 — Duplicate-warning paneel. Toont sterke matches
              (exact e-mail/telefoon) prominent + zachte matches (naam/bedrijf)
              als informatie. Medewerker kan bestaande openen OF bewust nieuw
              aanmaken. Naam-only matches blokkeren nooit. */}
          {duplicates && (duplicates.has_strong || duplicates.has_soft) && (
            <div className="mt-4 space-y-3" data-testid="klant-duplicate-warning">
              {duplicates.has_strong && (
                <div className="border-l-4 border-red-500 bg-red-50 p-3" data-testid="klant-duplicate-strong">
                  <div className="font-chivo font-black text-xs uppercase tracking-wider text-red-800 mb-2">
                    Sterke match — mogelijk zelfde klant
                  </div>
                  <div className="text-xs text-red-900 mb-2">
                    De volgende klant(en) hebben exact hetzelfde e-mailadres of telefoonnummer.
                    Open de bestaande klant OF bevestig bewust een duplicaat.
                  </div>
                  <ul className="space-y-2">
                    {duplicates.strong.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 bg-white border border-red-200 p-2" data-testid={`klant-duplicate-strong-item-${s.id}`}>
                        <div className="text-xs">
                          <div className="font-medium text-[#0A192F]">
                            {s.display_name || [s.first_name, s.last_name].filter(Boolean).join(" ") || s.company_name || "Onbekend"}
                          </div>
                          <div className="text-gray-600">
                            {s.email || "—"} · {s.phone || s.mobile || "—"}
                          </div>
                          <div className="text-[10px] text-red-700 uppercase tracking-wide mt-0.5">
                            Match op {s.matched_field === "email" ? "e-mail" : s.matched_field === "phone" ? "telefoon" : s.matched_field}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setCreating(false); setDuplicates(null); setDupBlocked(false); setSelected(s.id); }}
                          data-testid={`klant-duplicate-open-${s.id}`}
                          className="whitespace-nowrap bg-[#050505] hover:bg-[#0A192F] text-white text-[10px] uppercase font-chivo font-bold tracking-wide px-3 py-1.5"
                        >
                          Bestaande openen
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {duplicates.has_soft && (
                <div className="border-l-4 border-amber-400 bg-amber-50 p-3" data-testid="klant-duplicate-soft">
                  <div className="font-chivo font-black text-xs uppercase tracking-wider text-amber-800 mb-2">
                    Mogelijke overeenkomst — enkel informatie
                  </div>
                  <div className="text-xs text-amber-900 mb-2">
                    De naam of bedrijfsnaam lijkt op bestaande klant(en). Blokkeert niet.
                  </div>
                  <ul className="space-y-1">
                    {duplicates.soft.slice(0, 5).map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 text-xs bg-white border border-amber-200 p-2" data-testid={`klant-duplicate-soft-item-${s.id}`}>
                        <div>
                          <span className="font-medium text-[#0A192F]">
                            {s.display_name || [s.first_name, s.last_name].filter(Boolean).join(" ") || s.company_name || "Onbekend"}
                          </span>
                          <span className="text-gray-500 ml-2">
                            {s.email || s.phone || "—"}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setCreating(false); setDuplicates(null); setSelected(s.id); }}
                          data-testid={`klant-duplicate-soft-open-${s.id}`}
                          className="text-[10px] uppercase font-chivo font-bold tracking-wide text-[#0055FF] hover:underline"
                        >
                          Openen →
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2 bg-white border border-gray-200 p-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="klanten-search" placeholder="Zoeken op naam, e-mail, telefoon..." value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="klanten-filter-status" className="rounded-none w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeF} onValueChange={setTypeF}>
          <SelectTrigger data-testid="klanten-filter-type" className="rounded-none w-48"><SelectValue placeholder="Klanttype" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle typen</SelectItem>
            {PRIMARY_TYPES.map((k) => <SelectItem key={k} value={k} data-testid={`klanten-filter-type-${k}`}>{TYPE_LABELS[k]}</SelectItem>)}
            <div className="border-t border-gray-100 my-1" />
            {LEGACY_TYPES.map((k) => <SelectItem key={k} value={k} data-testid={`klanten-filter-type-${k}`}>{TYPE_LABELS[k]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* P0.2 UX guard — filter-active indicator zodat een lege lijst
          nooit als "klant verdwenen" wordt geïnterpreteerd. Toont welke
          filters actief zijn met 1-klik reset. */}
      {(q || statusF !== "all" || typeF !== "all") && (
        <div
          data-testid="klanten-filter-active-banner"
          className="mt-3 flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 p-2 pl-3 text-xs"
        >
          <span className="font-chivo font-bold uppercase tracking-wide text-amber-900">
            Filter actief:
          </span>
          {q && (
            <span className="inline-flex items-center gap-1 bg-white border border-amber-300 px-2 py-0.5" data-testid="klanten-filter-chip-q">
              zoek: &quot;{q}&quot;
              <button type="button" onClick={() => setQ("")} className="text-amber-700 hover:text-red-600" aria-label="Wis zoekterm">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {statusF !== "all" && (
            <span className="inline-flex items-center gap-1 bg-white border border-amber-300 px-2 py-0.5" data-testid="klanten-filter-chip-status">
              status: {STATUS_LABELS[statusF] || statusF}
              <button type="button" onClick={() => setStatusF("all")} className="text-amber-700 hover:text-red-600" aria-label="Wis statusfilter">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {typeF !== "all" && (
            <span className="inline-flex items-center gap-1 bg-white border border-amber-300 px-2 py-0.5" data-testid="klanten-filter-chip-type">
              type: {TYPE_LABELS[typeF] || typeF}
              <button type="button" onClick={() => setTypeF("all")} className="text-amber-700 hover:text-red-600" aria-label="Wis typefilter">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          <button
            type="button"
            data-testid="klanten-filter-reset-all"
            onClick={() => { setQ(""); setStatusF("all"); setTypeF("all"); }}
            className="ml-auto font-chivo font-bold uppercase text-[10px] tracking-wide text-amber-900 hover:text-black underline"
          >
            Alle filters wissen
          </button>
        </div>
      )}

      <div className="mt-4 bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500">Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="klanten-empty-state">
            {(q || statusF !== "all" || typeF !== "all") ? (
              <>
                <div className="font-chivo font-bold text-[#0A192F] text-sm">Geen klanten gevonden binnen de actieve filters.</div>
                <div className="mt-1 text-xs">Wis de filters hierboven om alle klanten te zien.</div>
                <button
                  type="button"
                  data-testid="klanten-empty-reset"
                  onClick={() => { setQ(""); setStatusF("all"); setTypeF("all"); }}
                  className="mt-3 inline-block bg-[#050505] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-[11px] tracking-wide px-4 py-2"
                >
                  Alle filters wissen
                </button>
              </>
            ) : (
              "Geen klanten gevonden."
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-gray-200 text-left">
              <tr>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Naam</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">E-mail / telefoon</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Type</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Status</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Bron</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Aangemaakt</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`klant-row-${c.id}`} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(c.id)}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {c.company_name ? <Building2 className="w-4 h-4 text-gray-400" /> : <User className="w-4 h-4 text-gray-400" />}
                      <div>
                        <div className="font-medium text-[#0A192F]">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</div>
                        {c.company_name && <div className="text-xs text-gray-500">{c.company_name}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    <div>{c.email || "—"}</div>
                    {c.phone && <div className="text-gray-500">{c.phone}</div>}
                  </td>
                  <td className="p-3 text-xs"><CustomerTypeBadge type={c.customer_type} size="sm" testid={`klant-row-${c.id}-type`} /></td>
                  <td className="p-3"><StatusBadge status={c.status} /></td>
                  <td className="p-3 text-xs text-gray-500">{SOURCE_LABELS[c.source] || c.source || "—"}</td>
                  <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{c.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right">
                    {canDelete && (
                      <button onClick={(e) => { e.stopPropagation(); onDelete(c.id); }} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`klant-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
